import {
  formatDuration,
  formatRange,
  minutesToHHMM,
  type ScheduleLesson,
} from '@skolveckan/shared';
import { subjectColors } from '../lib/subjects';
import { IconClock } from './Icons';

interface Props {
  current: ScheduleLesson | null;
  next: ScheduleLesson | null;
  nowMin: number;
  isToday: boolean;
  onOpen: (lesson: ScheduleLesson) => void;
}

/**
 * Svarar på frågan appen finns för: vad gör jag nu? Kortet har fyra lägen —
 * pågående lektion, rast, före skoldagen och efter. Ett tomt kort vore det
 * enda riktiga felet här.
 */
export function NowCard({ current, next, nowMin, isToday, onOpen }: Props) {
  if (!isToday) {
    return (
      <section className="nowcard nowcard--quiet">
        <p className="nowcard__quiettext">
          Du tittar på en annan dag än idag. Gå tillbaka till Idag för att se nu-läget.
        </p>
      </section>
    );
  }

  if (current && current.kind === 'lesson') {
    const colors = subjectColors(current.subject?.colorKey);
    const total = Math.max(current.endMin - current.startMin, 1);
    const elapsed = Math.min(Math.max(nowMin - current.startMin, 0), total);
    const left = total - elapsed;

    return (
      <section className="nowcard" aria-label="Pågående lektion">
        <span className="nowcard__spine" style={{ background: colors.line }} />
        <div className="nowcard__body">
          <div className="nowcard__head">
            <span className="chip chip--now">NU</span>
            <h2 className="nowcard__subject" style={{ color: colors.ink }}>
              {current.subject?.name ?? 'Lektion'}
            </h2>
            <span className="nowcard__meta">
              {formatRange(current.startMin, current.endMin)}
              {current.room ? ` · ${current.room}` : ''}
            </span>
          </div>
          <p className="nowcard__title">
            {current.title ?? 'Läraren har inte lagt in något innehåll för den här lektionen.'}
          </p>
          <div className="progress">
            <div
              className="progress__fill"
              style={{ width: `${Math.round((elapsed / total) * 100)}%`, background: colors.line }}
            />
          </div>
        </div>
        <div className="nowcard__aside">
          <div className="nowcard__left">
            <span className="nowcard__leftnum">{left}</span>
            <span className="nowcard__leftunit">min kvar</span>
          </div>
          <button type="button" className="button button--primary" onClick={() => onOpen(current)}>
            Öppna lektionen
          </button>
        </div>
      </section>
    );
  }

  if (current && current.kind !== 'lesson') {
    const left = current.endMin - nowMin;
    return (
      <section className="nowcard nowcard--break" aria-label="Rast">
        <span className="nowcard__spine" style={{ background: 'var(--subject-break-line)' }} />
        <div className="nowcard__body">
          <div className="nowcard__head">
            <IconClock />
            <h2 className="nowcard__subject">
              {current.kind === 'lunch' ? 'Lunch' : 'Rast'} till {minutesToHHMM(current.endMin)}
            </h2>
          </div>
          <p className="nowcard__title">
            {formatDuration(left)} kvar.{' '}
            {next
              ? `Sen ${next.subject?.name ?? 'lektion'} i ${next.room ?? 'sal'}.`
              : 'Sen är skoldagen slut.'}
          </p>
        </div>
        {next && (
          <div className="nowcard__aside">
            <button type="button" className="button button--ghost" onClick={() => onOpen(next)}>
              Se nästa lektion
            </button>
          </div>
        )}
      </section>
    );
  }

  if (next) {
    return (
      <section className="nowcard nowcard--quiet" aria-label="Innan skoldagen">
        <div className="nowcard__body">
          <div className="nowcard__head">
            <IconClock />
            <h2 className="nowcard__subject">
              {next.subject?.name ?? 'Första lektionen'} börjar {minutesToHHMM(next.startMin)}
            </h2>
          </div>
          <p className="nowcard__title">
            Om {formatDuration(next.startMin - nowMin)}
            {next.room ? ` · ${next.room}` : ''}
          </p>
        </div>
        <div className="nowcard__aside">
          <button type="button" className="button button--primary" onClick={() => onOpen(next)}>
            Öppna lektionen
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="nowcard nowcard--quiet" aria-label="Skoldagen slut">
      <div className="nowcard__body">
        <h2 className="nowcard__subject">Skoldagen är slut</h2>
        <p className="nowcard__title">
          Kolla uppgiftslistan om du har något kvar att göra till imorgon.
        </p>
      </div>
    </section>
  );
}
